import { defineComponent as _defineComponent } from 'vue'
import {computed, inject, onMounted, reactive, ref, watch} from "vue";
import CollapsableCard from "@/components/bootstrap/CollapsableCard.vue";
import {NutrientType} from "@/models/Ingredient/NutrientType";
import {LabelConfigurationModel} from "@/models/NutritionLabels/v2/LabelConfigurationModel";
import RecipesRepository from "@/utils/API/RecipesRepository";
import ListRequest, {Filter, Ordering, OrderType} from "@/models/ListRequest";
import {useNotify} from "@/hooks/useNotify";
import {ItemCompact} from "@/models/ItemCompact";
import {LabelConfigurationOption} from "@/models/NutritionLabels/v2/enums/LabelConfigurationOption";
import {debounce} from "@/utils/Helpers";
import {useTranslations} from "@/hooks/useTranslations";
import {
  getVoluntaryNutrients,
  OPTIONAL_CORE_NUTRIENTS,
  SUPPLEMENT_NUTRIENTS_US,
  SUPPLEMENT_NUTRIENTS_CANADA,
  OPTIONAL_CORE_SUPPLEMENT_NUTRIENTS, OPTIONAL_US_CORE_NUTRIENTS
} from "@/models/NutritionLabels/v2/collections/NutrientGroups";
import {Blend} from "@/models/NutritionLabels/v2/Blend";
import {NutritionLabelCountry} from "@/models/NutritionLabels/NutritionLabelCountry";

type UiRecipeRow = {
  rowId: string;
  recipeId: number | null;
  name: string;
  servingSizeGrams: number;
};


export default /*#__PURE__*/_defineComponent({
  __name: 'LabelConfiguration',
  props: {
    model: null,
    requestedConfigurationOptions: null,
    country: null,
    shouldExpand: { type: Boolean }
  },
  emits: ["update:model", "refreshLabel"],
  setup(__props: any, { emit }) {

const props = __props as {
  model: LabelConfigurationModel;
  requestedConfigurationOptions: LabelConfigurationOption[];
  country: NutritionLabelCountry;
  shouldExpand: boolean;
};



const MAX_RECIPES_AGGREGATE_LABEL = 4;
const uiRecipes = ref<UiRecipeRow[]>([]);

const fetchPreview: () => void = inject("bopFetchPreview");

// Local copy of the model
const localModel = reactive<LabelConfigurationModel>(
  JSON.parse(JSON.stringify(props.model))
);

const notify = useNotify();
const translations = useTranslations();
const recipeOptions = ref<ItemCompact[]>([]);

const coreNutrientsByCountry = computed(() => {
  switch (props.country) {
  case NutritionLabelCountry.Canada:
    return OPTIONAL_CORE_NUTRIENTS;

  case NutritionLabelCountry.US:
    if(props.requestedConfigurationOptions.includes(LabelConfigurationOption.SUPPLEMENT_NUTRIENTS)) {
      return OPTIONAL_CORE_SUPPLEMENT_NUTRIENTS;
    }
    else{
      return OPTIONAL_US_CORE_NUTRIENTS;
    }

  default:
    return [];
  }
});

const supplementNutrientsByCountry = computed(() => {
  switch (props.country) {
  case NutritionLabelCountry.Canada:
    return SUPPLEMENT_NUTRIENTS_CANADA;

  case NutritionLabelCountry.US:
    if(props.requestedConfigurationOptions.includes(LabelConfigurationOption.SUPPLEMENT_NUTRIENTS)) {
      return SUPPLEMENT_NUTRIENTS_US;
    }
    else{
      return OPTIONAL_US_CORE_NUTRIENTS;
    }

  default:
    return [];
  }
});

watch(
  () => props.model,
  (val) => {
    Object.assign(localModel, val);
    syncRecipesFromModel();
  },
  { immediate: true }
);

watch(
  () => recipeOptions.value,
  () => {
    if (!recipeOptions.value.length) return;
    syncRecipesFromModel();
  }
);

watch(
  () => props.country,
  () => {
    localModel.selectedCoreNutrients = localModel.selectedCoreNutrients.filter((n) =>
      coreNutrientsByCountry.value.includes(n)
    );

    localModel.selectedSupplementNutrients = localModel.selectedSupplementNutrients.filter((n) =>
      supplementNutrientsByCountry.value.includes(n)
    );

    emitUpdate();
  }
);

watch(
  () => props.requestedConfigurationOptions,
  (newVal) => {
    const hasAnyQuantity = newVal.includes(LabelConfigurationOption.RECIPES_AND_UNIT_ANY_QUANTITY);
    const hasTwoOnly = newVal.includes(LabelConfigurationOption.RECIPES_AND_UNIT_TWO);

    const max = hasTwoOnly ? 2 : hasAnyQuantity ? MAX_RECIPES_AGGREGATE_LABEL : 1;

    if (uiRecipes.value.length > max) {
      uiRecipes.value.splice(max);
      syncModelRecipesFromUi();
      emitUpdate();
    }

    if (uiRecipes.value.length === 0 && (hasAnyQuantity || hasTwoOnly) && !props.model.recipes?.length) {
      uiRecipes.value.push({
        rowId: crypto.randomUUID(),
        recipeId: null,
        name: "",
        servingSizeGrams: 100
      });
      syncModelRecipesFromUi();
      emitUpdate();
    }
  },
  { immediate: true }
);

onMounted(() => {
  fetchRecipeListing();
});

function fetchRecipeListing() {
  const defaultFilters: Filter[] = [{name: "", value: ""}];
  const defaultOrdering: Ordering = {field: "", orderType: OrderType.Asc};

  const reqData = new ListRequest({
    page: 1,
    pageSize: 0,
    filters: defaultFilters,
    orders: [defaultOrdering],
  });

  RecipesRepository.listReducedRecipes(reqData, true)
    .then((response) => {
      recipeOptions.value = response.data.result.map((r) => ({id: r.id, name: r.name, servingSizeGrams: 0})) || [];
    })
    .catch((error) => {
      if (error.response) {
        notify.error(
          error.response.data.errorMessages[0].userVisibleMessage
        );
      }
    });
}

function syncRecipesFromModel() {
  if (!recipeOptions.value.length) {
    return;
  }

  uiRecipes.value = props.model.recipes.map((r) => {
    const match = recipeOptions.value.find((opt) => opt.id === r.recipeId);

    return {
      rowId: crypto.randomUUID(),
      recipeId: r.recipeId,
      name: match?.name ?? "Unknown recipe",
      servingSizeGrams: Number(r.servingSizeGrams) || 100
    };
  });
}

function updateRecipe(
  row: UiRecipeRow,
  selected: ItemCompact | null
) {
  if (!selected) {
    row.recipeId = null;
    row.name = "";
  }
  else {
    row.recipeId = selected.id;
    row.name = selected.name;
  }

  syncModelRecipesFromUi();
  emitUpdate();
}

function emitUpdate() {
  console.log("emitting update", localModel);

  emit("update:model", localModel);
  debouncedFetchPreview();
}

function syncModelRecipesFromUi() {
  localModel.recipes = uiRecipes.value.map((r) => ({
    recipeId: r.recipeId,
    servingSizeGrams: String(r.servingSizeGrams)
  }));
}

function debouncedFetchPreview() {
  debounce(fetchPreview, 1000, "back-of-package-config-label-update");
}

function addBlendRow() {
  localModel.proprietaryBlends.push({ name: "", value: "", dailyValue: "", subBlends: [] });
  emitUpdate();
}

function removeBlendRow(index: number) {
  localModel.proprietaryBlends.splice(index, 1);
  emitUpdate();
}

function addSubBlendRow(blend: Blend) {
  blend.subBlends.push({
    unit: "",
    name: "",
    value: "",
    dailyValue: ""
  });
  emitUpdate();
}

function removeSubBlendRow(blend: Blend, index: number) {
  blend.subBlends.splice(index, 1);
  emitUpdate();
}

function shouldAllowAddRecipe() {
  const isTwoRecipeOnly = props.requestedConfigurationOptions.includes(LabelConfigurationOption.RECIPES_AND_UNIT_TWO);
  const maxRecipes = isTwoRecipeOnly ? 2 : MAX_RECIPES_AGGREGATE_LABEL; // temp max value for "arbitrary" mode

  return uiRecipes.value.length < maxRecipes;
}

function onServingSizeChange() {
  syncModelRecipesFromUi();
  emitUpdate();
}

function addRecipeRow() {
  if (!shouldAllowAddRecipe()) return;

  uiRecipes.value.push({
    rowId: crypto.randomUUID(),
    recipeId: null,
    name: "",
    servingSizeGrams: 100
  });

  syncModelRecipesFromUi();
  emitUpdate();
}

function removeRecipeRow(index: number) {
  uiRecipes.value.splice(index, 1);

  syncModelRecipesFromUi();
  emitUpdate();
}

return { __sfc: true,props, MAX_RECIPES_AGGREGATE_LABEL, uiRecipes, emit, fetchPreview, localModel, notify, translations, recipeOptions, coreNutrientsByCountry, supplementNutrientsByCountry, fetchRecipeListing, syncRecipesFromModel, updateRecipe, emitUpdate, syncModelRecipesFromUi, debouncedFetchPreview, addBlendRow, removeBlendRow, addSubBlendRow, removeSubBlendRow, shouldAllowAddRecipe, onServingSizeChange, addRecipeRow, removeRecipeRow, CollapsableCard, NutrientType, LabelConfigurationOption, getVoluntaryNutrients }
}

})